import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, Redirect } from "react-router-dom";
import { Box, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import moment from "moment";
import MaterialTable from "material-table";
import { getLocalization } from "util/table-utils";
import { mapTableData } from "util/helpers";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import DarkLayout from "components/Shared/DarkLayout";
import { Input } from "components/Shared/mui-formik-inputs";
import {
  getMobileLogs,
  selectDiagnosticsLoading,
  selectMobileLogs,
  selectMobileLogsPagination,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  filters: {
    marginBottom: 16,
    "& .MuiFilledInput-root": {
      backgroundColor: "#1F1F1F",
    },
    "& .MuiFilledInput-root:hover": {
      backgroundColor: "#262626",
    },
    "& .MuiFilledInput-root.Mui-focused": {
      backgroundColor: "#262626",
    },
    "& .MuiInputBase-input": {
      color: "#F5F5F5",
    },
    "& .MuiFormLabel-root": {
      color: "#ADADAD",
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#6F9CEB",
    },
    "& .MuiFormHelperText-root": {
      color: "#ADADAD",
    },
  },
  detailPanel: {
    backgroundColor: "#1F1F1F",
    padding: "16px 24px",
    color: "#F5F5F5",
  },
  detailLabel: {
    color: "#ADADAD",
    font: "normal normal normal 12px/20px Roboto",
    marginBottom: 4,
  },
  detailValue: {
    color: "#F5F5F5",
    font: "normal normal normal 14px/20px Roboto",
    marginBottom: 16,
  },
  payload: {
    color: "#F5F5F5",
    font: "normal normal normal 12px/18px monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0,
  },
});

const tableTitle = "MOBILE LOGS";

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const formatPayload = (payload) => {
  if (!payload) {
    return "-";
  }
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch (error) {
    return payload;
  }
};

const DiagnosticsMobileRouteLogs = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { routeId } = useParams();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const mobileLogs = useSelector(selectMobileLogs);
  const pagination = useSelector(selectMobileLogsPagination);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [level, setLevel] = useState("");

  useEffect(() => {
    if (!isDiagnosticsVisible(user) || !routeId) {
      return;
    }
    dispatch(
      getMobileLogs({
        page: page + 1,
        limit: pageSize,
        route_id: routeId,
        level: level || undefined,
      }),
    );
  }, [dispatch, user, routeId, page, pageSize, level]);

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  const columns = [
    { title: t("Id"), field: "id" },
    { title: t("Event"), field: "event_type" },
    { title: t("Level"), field: "level" },
    {
      title: t("Payload"),
      field: "payload",
      render: (rowData) => (
        <span
          style={{
            maxWidth: 360,
            display: "inline-block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.payload}
        </span>
      ),
    },
    {
      title: t("Client Time"),
      field: "client_timestamp",
      render: (rowData) =>
        rowData.client_timestamp
          ? moment(rowData.client_timestamp).format("DD.MM.YYYY HH:mm:ss")
          : "-",
    },
    {
      title: t("Created"),
      field: "created_at",
      render: (rowData) =>
        rowData.created_at ? moment(rowData.created_at).format("DD.MM.YYYY HH:mm:ss") : "-",
    },
  ];

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <DarkLayout doublebar loading={false}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {t("Route ID")} {routeId}
          </Typography>
          <Box className={classes._buttonbox} component="div">
            <CloseIcon
              onClick={() => history.push(PATHS.diagnostics.mobile)}
              className={classes._edit}
            />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Close")}
            </Typography>
          </Box>
        </div>

        <Grid container spacing={2} className={classes.filters}>
          <Grid item xs={12} md={3}>
            <Input
              name="level"
              label={t("Level")}
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                setPage(0);
              }}
              help="debug | info | warn | error | crash"
            />
          </Grid>
        </Grid>

        <div className="custom-table-styles">
          <MaterialTable
            style={{ display: "flex", flexDirection: "column" }}
            isLoading={loading}
            data={mapTableData(mobileLogs)}
            title={t(tableTitle)}
            columns={columns}
            localization={getLocalization(t)}
            totalCount={pagination?.total || 0}
            page={page}
            onChangePage={(nextPage) => setPage(nextPage)}
            onChangeRowsPerPage={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            detailPanel={[
              {
                tooltip: t("Details"),
                render: (rowData) => (
                  <Box className={classes.detailPanel}>
                    <Typography className={classes.detailLabel}>{t("Id")}</Typography>
                    <Typography className={classes.detailValue}>{rowData.id}</Typography>

                    <Typography className={classes.detailLabel}>{t("Event")}</Typography>
                    <Typography className={classes.detailValue}>
                      {rowData.event_type || "-"}
                    </Typography>

                    <Typography className={classes.detailLabel}>{t("Level")}</Typography>
                    <Typography className={classes.detailValue}>
                      {rowData.level || "-"}
                    </Typography>

                    <Typography className={classes.detailLabel}>{t("Client Time")}</Typography>
                    <Typography className={classes.detailValue}>
                      {rowData.client_timestamp
                        ? moment(rowData.client_timestamp).format("DD.MM.YYYY HH:mm:ss")
                        : "-"}
                    </Typography>

                    <Typography className={classes.detailLabel}>{t("Created")}</Typography>
                    <Typography className={classes.detailValue}>
                      {rowData.created_at
                        ? moment(rowData.created_at).format("DD.MM.YYYY HH:mm:ss")
                        : "-"}
                    </Typography>

                    <Typography className={classes.detailLabel}>{t("Payload")}</Typography>
                    <pre className={classes.payload}>{formatPayload(rowData.payload)}</pre>
                  </Box>
                ),
              },
            ]}
            options={{
              pageSize,
              pageSizeOptions: [20, 50, 100],
              actionsColumnIndex: -1,
              searchFieldAlignment: "left",
              showTitle: false,
              search: false,
              sorting: false,
              emptyRowsWhenPaging: false,
              headerStyle: {
                backgroundColor: "#121212",
                color: "white",
                borderBottom: "1px solid #525252",
                font: "normal normal normal 12px/24px Roboto",
                fontWeight: "bold",
              },
              cellStyle: {
                backgroundColor: "#121212",
                color: "white",
                border: "none",
                font: "normal normal normal 12px/24px Roboto",
                padding: "0 16px",
              },
              searchFieldStyle: {
                color: "#F5F5F5",
              },
              filterCellStyle: {
                color: "#F5F5F5",
              },
              rowStyle: { height: "38px" },
            }}
          />
        </div>
      </DarkLayout>
    </>
  );
};

export default DiagnosticsMobileRouteLogs;
